import React, { useCallback, useEffect, useMemo, useState } from "react";
import "../../pages/Dashboard.scss";

import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../store";
import {
  setProcurementFilters,
  resetProcurementFilters,
} from "../../store/filtersSlice";
import agentIcon from "../../assets/images/ic_agentIcon.svg";
import {
  Modal
} from "@mui/material";
import { InfoOutlined, SearchOff } from '@mui/icons-material';
import AgentInfoModal from "../AgentInfoModal";
import { Agents } from "../../types/master";

// The only agent that opens straight into the procurement "reviewer" filter
// state (Contract Review, Negotiation and Finalization Agent).
const PROCUREMENT_REVIEWER_AGENT_ID = 20;


type WorkflowsByFunctionProps = {
  onAgentClick: (agentId: number) => void;
};

export default function WorkflowsByFunction({
  onAgentClick,
}: WorkflowsByFunctionProps): JSX.Element {
  const allAgents = useSelector((state: RootState) => state.agents.data);
  const dispatch = useAppDispatch();

  // Category tabs come straight from whatever categories are actually present
  // on agents the backend returns (already filtered by is_display/is_deleted),
  // in first-seen order — no hardcoded category list to keep in sync.
  const categories = useMemo(() => {
    const seen = new Set<string>();
    const list: string[] = [];
    for (const agent of allAgents) {
      if (agent.category && !seen.has(agent.category)) {
        seen.add(agent.category);
        list.push(agent.category);
      }
    }
    return list;
  }, [allAgents]);

  const [activeCategory, setActiveCategory] = useState<string>("");

  useEffect(() => {
    if (!activeCategory && categories.length > 0) {
      setActiveCategory(categories[0]);
    }
  }, [categories, activeCategory]);

  // Helper function to construct video blob URL
  const getVideoUrl = useCallback((agent: Agents): string | null => {
    if (!agent.agent_intro_video_path) return null;
    return `${import.meta.env.VITE_TEMPLATE_BLOB_URL}/intro-videos/${agent.agent_intro_video_path}?${import.meta.env.VITE_TEMPLATE_G42_FILES}`;
  }, []);

  // Agents for the active category tab, straight from the DB-backed list
  const currentWorkflows = useMemo(
    () => allAgents.filter((agent) => agent.category === activeCategory),
    [activeCategory, allAgents],
  );

  // Helper function to check if an agent is launchable
  const isAgentAvailable = useCallback((agent: Agents) => {
    // Hidden agents (status = 0) keep their card visible but Launch stays disabled
    if (agent.status !== 1) return false;

    // Agent IDs that should open using their endpoint URL in a new tab
    const EXTERNAL_ENDPOINT_AGENTS = [19, 27, 31, 32, 33, 34, 503, 504, 505, 506, 507];

    // If agent requires external endpoint but doesn't have one, it's not available
    if (EXTERNAL_ENDPOINT_AGENTS.includes(agent.id) && !agent.endpoint) {
      return false;
    }

    return true;
  }, []);

  const getIconIMG = useCallback(() => {
    return <img src={agentIcon} alt="agent icon" />;
  }, []);

  const handleSelectAgent = useCallback(
    (agent: Agents) => {
      // Contract Review, Negotiation and Finalization Agent opens straight
      // into the procurement "reviewer" filter state.
      if (agent.id === PROCUREMENT_REVIEWER_AGENT_ID) {
        dispatch(setProcurementFilters({ procurementType: "reviewer" }));
      } else {
        dispatch(resetProcurementFilters());
      }
      onAgentClick(agent.id);
    },
    [onAgentClick, dispatch],
  );

  const [openVideoWorkflowId, setOpenVideoWorkflowId] = useState<number | null>(
    null,
  );

  const handleVideoClick = (agentId: number) => {
    setOpenVideoWorkflowId(agentId);
  };

  const closeVideoModal = () => {
    setOpenVideoWorkflowId(null);
  };

  const openVideoUrl = useMemo(() => {
    if (openVideoWorkflowId === null) return null;
    const agent = allAgents.find((a) => a.id === openVideoWorkflowId);
    return agent ? getVideoUrl(agent) : null;
  }, [openVideoWorkflowId, allAgents, getVideoUrl]);

  const [openAgentModal, setOpenAgentModal] = useState<boolean>(false);
  const [agentId, setAgentId] = useState<string | number | null>(null);

  const handleAgentInfoModal = (id: string | number) => {
    setOpenAgentModal(true);
    setAgentId(id);
  };
  const handleCloseAgentModal = () => setOpenAgentModal(false);

  return (
    <div className="card workflows">
      <div className="agent-function">
        <div className="card-header">
          <h3>
            Agents by Function
          </h3>
        </div>
        <p className="subtle">
          Explore reimagined processes for your function powered by agents.
        </p>
      </div>
      <div className="tabs">
        {categories.map((category) => (
          <button
            key={category}
            className={`tab ${activeCategory === category ? "active" : ""}`}
            onClick={() => setActiveCategory(category)}
          >
            {category}
          </button>
        ))}
      </div>
      <div className="agent-by-functions">
        <div className="workflow-grid">
          {currentWorkflows.length === 0 ? (
            <div className="no-results">
              <div>
                <SearchOff style={{ fontSize: "100px" }} />
              </div>
              <h4>No result found.</h4>
              <p>No agents in this category yet.</p>
            </div>
          ) : (
            currentWorkflows.map((agent) => (
              <div
                key={agent.id}
                className="workflow-item"
              >
                <div className="workflow-main">
                  <div className="workflow-header">
                    <div className="category-container">
                      <div className="category-img">
                        {getIconIMG()}
                      </div>

                      <div className="category-right">
                        <button onClick={() => handleAgentInfoModal(agent.id)}><InfoOutlined /></button>
                      </div>
                    </div>
                    <div className="workflow-info">
                      <div className="wf-title">{agent.agent_name}</div>
                    </div>
                  </div>
                </div>
                <div className="workflow-actions">
                  <button
                    className="agent-video-icon workflow-video-trigger"
                    onClick={() => handleVideoClick(agent.id)}
                    aria-label="Watch how it works"
                    disabled={!getVideoUrl(agent)}
                    style={{
                      opacity: !getVideoUrl(agent) ? 0.5 : 1,
                      cursor: !getVideoUrl(agent) ? "not-allowed" : "pointer",
                    }}
                  >
                    <span className="workflow-video-icon-tooltip">
                      How it works
                    </span>
                    ▶
                  </button>

                  <button
                    className="deploy"
                    onClick={() => handleSelectAgent(agent)}
                    disabled={!isAgentAvailable(agent)}
                    style={{
                      opacity: !isAgentAvailable(agent) ? 0.5 : 1,
                      cursor: !isAgentAvailable(agent) ? "not-allowed" : "pointer",
                    }}
                  >
                    <span>Launch</span>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M6 12L10 8L6 4"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <Modal
        open={openVideoWorkflowId !== null}
        onClose={closeVideoModal}
        className="agent-video-modal"
        aria-labelledby="workflow-video-modal-title"
        aria-describedby="workflow-video-modal-description"
        slotProps={{
          backdrop: {
            sx: { backgroundColor: "rgba(0, 0, 0, 0.6)" },
          },
        }}
      >
        <div className="agent-video-modal-content">
          <button
            className="agent-video-modal-close"
            onClick={(e) => {
              e.stopPropagation();
              closeVideoModal();
            }}
            aria-label="Close video modal"
          >
            ✕
          </button>
          {openVideoWorkflowId !== null && (
            <video
              src={openVideoUrl || ""}
              controls
              autoPlay
              className="agent-video-modal-player"
            />
          )}
        </div>
      </Modal>

      <AgentInfoModal
        open={openAgentModal}
        onClose={handleCloseAgentModal}
        agentId={agentId}
        onAgentClick={onAgentClick}
      />
    </div>
  );
}
